import { Box, Flex, Input } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { AddressAutocomplete, IParsedPlace } from '../../../../components/googleMaps/AddressAutocomplete'
import { EventLocationMap } from '../../../../components/googleMaps/EventLocationMap'
import { GoogleMapsProvider } from '../../../../components/googleMaps/GoogleMapsProvider'
import { MaskedInput, useZipCodeLookup } from '../../../../components/MaskedInput'

export type LocationValue = {
  zipcode: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  latitude: number | null
  longitude: number | null
  place_id?: string | null
  place_name?: string | null
}

interface IProps {
  value: LocationValue
  onChange: (patch: Partial<LocationValue>) => void
  disabled?: boolean
  errors?: Record<string, string[]>
  /** Prefix for the backend validation error keys (e.g. "editions.0."). */
  errorPrefix?: string
}

/** Address block (autocomplete + manual fields + map) shared by series and editions. */
export function LocationFields({ value, onChange, disabled, errors = {}, errorPrefix = '' }: IProps) {
  const { loadingZipCode, lookup: lookupZipCode } = useZipCodeLookup({
    onAddressFound: (address) => onChange(address),
    onCoordinatesFound: (latitude, longitude) => onChange({ latitude, longitude })
  })

  const err = (field: string) => errors[`${errorPrefix}${field}`]?.[0]

  const handleZipcode = (zipcode: string) => {
    onChange({ zipcode })
    lookupZipCode(zipcode)
  }

  const handlePlaceSelected = (place: IParsedPlace) => {
    onChange({
      street: place.street,
      number: place.number,
      neighborhood: place.neighborhood,
      city: place.city,
      state: place.state,
      zipcode: place.zipcode,
      latitude: place.latitude,
      longitude: place.longitude,
      place_id: place.place_id,
      place_name: place.place_name
    })
  }

  const handleMapPositionChange = (lat: number, lng: number, clickedPlaceId?: string) => {
    onChange({ latitude: lat, longitude: lng })

    if (!window.google?.maps) return

    const apply = (components: google.maps.GeocoderAddressComponent[], placeId: string | null, name: string | null) => {
      const get = (type: string, useShort = false) => {
        const comp = components.find((c) => c.types.includes(type))
        return comp ? (useShort ? comp.short_name : comp.long_name) : ''
      }
      onChange({
        street: get('route'),
        number: get('street_number'),
        neighborhood: get('sublocality_level_1') || get('sublocality') || get('neighborhood'),
        city: get('administrative_area_level_2'),
        state: get('administrative_area_level_1', true),
        zipcode: get('postal_code')?.replace('-', '') || '',
        place_id: placeId,
        place_name: name
      })
    }

    if (clickedPlaceId) {
      const placesService = new google.maps.places.PlacesService(document.createElement('div'))
      placesService.getDetails({ placeId: clickedPlaceId, fields: ['name', 'address_components'] }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          apply(place.address_components || [], clickedPlaceId, place.name ?? null)
        }
      })
      return
    }

    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        onChange({ place_id: null, place_name: null })
        return
      }
      apply(results[0].address_components, results[0].place_id ?? null, null)
    })
  }

  return (
    <GoogleMapsProvider>
      <Box>
        <FormField label="Buscar por nome do local">
          <AddressAutocomplete onPlaceSelected={handlePlaceSelected} disabled={disabled} />
        </FormField>
      </Box>

      <Flex gap={3} mt={3}>
        <FormField label="CEP" error={err('zipcode')} isRequired w="160px">
          <MaskedInput
            mask="zipCode"
            value={value.zipcode}
            onChange={handleZipcode}
            isDisabled={disabled || loadingZipCode}
          />
        </FormField>
        <FormField label="Logradouro" error={err('street')} flex="3">
          <Input
            value={value.street}
            onChange={(e) => onChange({ street: e.target.value })}
            disabled={disabled}
            isReadOnly={loadingZipCode}
          />
        </FormField>
        <FormField label="Número" error={err('number')} w="100px">
          <Input value={value.number} onChange={(e) => onChange({ number: e.target.value })} disabled={disabled} />
        </FormField>
      </Flex>

      <Flex gap={3} mt={3}>
        <Box flex="1">
          <FormField label="Complemento">
            <Input
              value={value.complement}
              onChange={(e) => onChange({ complement: e.target.value })}
              disabled={disabled}
            />
          </FormField>
        </Box>
        <FormField label="Bairro" error={err('neighborhood')} flex="1">
          <Input
            value={value.neighborhood}
            onChange={(e) => onChange({ neighborhood: e.target.value })}
            disabled={disabled}
            isReadOnly={loadingZipCode}
          />
        </FormField>
        <FormField label="Cidade" error={err('city')} flex="1">
          <Input
            value={value.city}
            onChange={(e) => onChange({ city: e.target.value })}
            disabled={disabled}
            isReadOnly={loadingZipCode}
          />
        </FormField>
        <FormField label="UF" error={err('state')} w="80px">
          <Input
            value={value.state}
            onChange={(e) => onChange({ state: e.target.value })}
            disabled={disabled}
            maxLength={2}
            isReadOnly={loadingZipCode}
          />
        </FormField>
      </Flex>

      <Flex gap={3} mt={3}>
        <Box flex="1">
          <FormField label="Latitude">
            <Input value={value.latitude?.toString() ?? ''} isReadOnly placeholder="-" />
          </FormField>
        </Box>
        <Box flex="1">
          <FormField label="Longitude">
            <Input value={value.longitude?.toString() ?? ''} isReadOnly placeholder="-" />
          </FormField>
        </Box>
      </Flex>

      <Box mt={3}>
        <FormField label="Arraste o pin ou clique no mapa para definir a localização">
          <EventLocationMap
            latitude={value.latitude}
            longitude={value.longitude}
            onPositionChange={handleMapPositionChange}
          />
        </FormField>
      </Box>
    </GoogleMapsProvider>
  )
}
